/* eslint-disable react/prop-types */
import React, { useContext, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaCopy, FaCheck, FaKey, FaFlask, FaDatabase } from 'react-icons/fa';
import { globalContext } from '../Context/GlobalContext';
import { authContext } from '../Context/AuthContext';
import styles from './ApiInfo.module.css';

// Ejemplo de respuesta (la comilla de las pulgadas va escapada para que sea un JSON válido)
const EJEMPLO = `{
  "Alias": "LSXXZZCCALX",
  "Numero_de_Parte": "LS22D300GALXZB",
  "Descripcion": "MONITOR SAMSUNG FLAT 22\\" IPS 100HZ",
  "Precio": 77,
  "Moneda": "DOLARES",
  "Cotizacion": 1515,
  "Tasa_IVA": "21",
  "Tasa_Impuestos_Internos": 10.5,
  "Stock": 2286,
  "Marca": "SAMSUNG",
  "Categoria": "MONITORES"
}`;

const EMAIL_SOPORTE = 'soporteweb@solutionbox.com.ar';

// Cuerpo del correo: los datos que hay que completar
const CUERPO_CORREO = [
    'Número de cliente',
    'Razón social',
    'Correo electrónico del administrador',
    'Nombre y apellido del responsable de la integración',
].join('\r\n');

const DATOS = ['datos-1', 'datos-2', 'datos-3', 'datos-4'];
const RECIBIR = ['recibir-1', 'recibir-2', 'recibir-3', 'recibir-4'];
const CAMPOS = [
    'campo-1', 'campo-2', 'campo-3', 'campo-4', 'campo-5',
    'campo-6', 'campo-7', 'campo-8', 'campo-9', 'campo-10',
];

const ApiInfo = () => {
    const [t] = useTranslation('global');
    const { colorTheme } = useContext(globalContext);
    const { user } = useContext(authContext);

    // Asunto: SOLICITUD ALTA API - NOMBRE Y APELLIDO - NUMERO DE CLIENTE (datos del usuario logueado)
    const asunto = ['SOLICITUD ALTA API', user && `${user.Nombre || ''} ${user.Apellido || ''}`.trim(), user && user.Cliente]
        .filter(Boolean)
        .join(' - ');
    const mailto = `mailto:${EMAIL_SOPORTE}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(CUERPO_CORREO)}`;
    const [copiado, setCopiado] = useState(false);
    const temporizador = useRef(null);

    useEffect(() => () => clearTimeout(temporizador.current), []);

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(EJEMPLO);
            setCopiado(true);
            clearTimeout(temporizador.current);
            temporizador.current = setTimeout(() => setCopiado(false), 2000);
        } catch (err) {
            console.error('No se pudo copiar el ejemplo', err);
        }
    };

    return (
        <div className={styles.root} data-theme={colorTheme === 'dark' ? 'dark' : 'light'}>
            {/* ===== Cabecera ===== */}
            <div className={styles.hero}>
                <div className={`${styles.wrap} ${styles.heroInner}`}>
                    <div className={styles.kicker}>{t('apiInfo.kicker')}</div>
                    <h1>{t('apiInfo.titulo')}</h1>
                    <p>{t('apiInfo.gracias')}</p>
                </div>
            </div>

            <div className={`${styles.wrap} ${styles.cuerpo}`}>
                {/* ===== Cómo comenzar ===== */}
                <div className={styles.pasos}>
                    <div className={styles.card}>
                        <div className={styles.paso}>1</div>
                        <h2>{t('apiInfo.paso1-titulo')}</h2>
                        <p>{t('apiInfo.paso1-intro')}</p>
                        <ul className={styles.checks}>
                            {DATOS.map((k) => (
                                <li key={k}>
                                    <FaCheckCircle aria-hidden="true" /> {t(`apiInfo.${k}`)}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.paso}>2</div>
                        <h2>{t('apiInfo.paso2-titulo')}</h2>
                        <p>{t('apiInfo.paso2-intro')}</p>
                        <ul className={styles.checks}>
                            {RECIBIR.map((k) => (
                                <li key={k}>
                                    <FaCheckCircle aria-hidden="true" /> {t(`apiInfo.${k}`)}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* ===== Qué permite consultar ===== */}
                <div className={`${styles.card} ${styles.cardAncha}`}>
                    <div className={styles.cardCab}>
                        <span className={styles.icono} aria-hidden="true"><FaDatabase /></span>
                        <h2>{t('apiInfo.consulta-titulo')}</h2>
                    </div>
                    <p>{t('apiInfo.consulta-intro')}</p>

                    <ul className={styles.chips}>
                        {CAMPOS.map((k) => (
                            <li key={k}>{t(`apiInfo.${k}`)}</li>
                        ))}
                    </ul>

                    <p className={styles.ejemploIntro}>{t('apiInfo.ejemplo-intro')}</p>
                    <div className={styles.codigo}>
                        <div className={styles.codigoBarra}>
                            <span>GET · JSON</span>
                            <button type="button" onClick={copiar}>
                                {copiado ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
                                {copiado ? t('apiInfo.copiado') : t('apiInfo.copiar')}
                            </button>
                        </div>
                        <pre><code>{EJEMPLO}</code></pre>
                    </div>
                </div>

                {/* ===== Autenticación y laboratorio ===== */}
                <div className={styles.pasos}>
                    <div className={styles.card}>
                        <div className={styles.cardCab}>
                            <span className={styles.icono} aria-hidden="true"><FaKey /></span>
                            <h2>{t('apiInfo.auth-titulo')}</h2>
                        </div>
                        <p>{t('apiInfo.auth-texto')}</p>
                        <div className={styles.dato}>
                            <strong>2</strong>
                            <span>{t('apiInfo.auth-frecuencia')}</span>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardCab}>
                            <span className={styles.icono} aria-hidden="true"><FaFlask /></span>
                            <h2>{t('apiInfo.lab-titulo')}</h2>
                        </div>
                        <p>{t('apiInfo.lab-texto-1')}</p>
                        <p>{t('apiInfo.lab-texto-2')}</p>
                    </div>
                </div>

                {/* ===== Cierre ===== */}
                <div className={styles.cierre}>
                    <a href={mailto} className={styles.boton}>
                        {t('apiInfo.cta')}
                    </a>
                    <small>
                        {t('apiInfo.cta-alternativa')} <a href={`mailto:${EMAIL_SOPORTE}`}>{EMAIL_SOPORTE}</a>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default ApiInfo;
